import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Bell, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const GROUP_LABELS = { entity_admin: 'Entity Admins', back_office: 'Back Office' };

export const EmailNotificationsConfig = ({ bare = false } = {}) => {
  const [catalog, setCatalog] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');

  const [testOpen, setTestOpen] = useState(false);
  const [testCtx, setTestCtx] = useState({ notification: null, entity_id: '', entity_name: '' });
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/email-notifications/config`, { withCredentials: true, headers: authHeaders() });
      setCatalog(data?.catalog || []);
      setRows(data?.entities || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load email notifications'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const persist = async (entity_id, notification_key, patch, entity_name) => {
    const key = `${entity_id}:${notification_key}:${Object.keys(patch)[0]}`;
    setSavingKey(key);
    try {
      await axios.put(
        `${API_URL}/api/email-notifications/config/${entity_id}/${notification_key}`,
        patch,
        { withCredentials: true, headers: authHeaders() },
      );
      setRows(rs => rs.map(r => {
        if (r.entity_id !== entity_id) return r;
        const notif = r.notifications[notification_key] || {};
        return {
          ...r,
          notifications: {
            ...r.notifications,
            [notification_key]: {
              enabled: patch.enabled !== undefined ? patch.enabled : notif.enabled,
              groups: patch.groups !== undefined ? patch.groups : notif.groups,
            },
          },
        };
      }));
      toast.success(`Saved for ${entity_name}`);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save'));
    } finally {
      setSavingKey('');
    }
  };

  const openTest = (notification, row) => {
    setTestCtx({ notification, entity_id: row.entity_id, entity_name: row.entity_name });
    setTestEmail('');
    setTestOpen(true);
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error('Enter recipient email'); return; }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/email-notifications/test/${testCtx.notification.key}`,
        { entity_id: testCtx.entity_id, to_email: testEmail.trim() },
        { withCredentials: true, headers: authHeaders() },
      );
      toast.success(data?.message || 'Test email sent');
      setTestOpen(false);
    } catch (e) {
      toast.error(formatApiError(e, 'Test send failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={bare ? '' : 'bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6'} data-testid="email-notifications-config">
      {!bare && (
        <>
          <div className="flex items-center gap-2 mb-1">
            <Bell size={20} className="text-[#0A2540]" />
            <h2 className="text-lg font-semibold text-[#0A2540] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Email Notifications
            </h2>
          </div>
          <p className="text-sm text-[#78716C] mb-5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Enable outbound email notifications per entity. Select which user groups receive each notification, or send a live test to verify formatting.
          </p>
        </>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-[#78716C]" data-testid="email-notifications-loading">Loading…</div>
      ) : catalog.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#78716C]">No notifications available.</div>
      ) : (
        <div className="space-y-6">
          {catalog.map(notif => (
            <div key={notif.key} className="border border-[#EDE6D8] rounded-lg overflow-hidden" data-testid={`notif-block-${notif.key}`}>
              <div className="bg-[#FCFAF6] px-4 py-3 border-b border-[#EDE6D8]">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#0A2540]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{notif.label}</span>
                  <Badge className="bg-[#E7F5EC] text-[#166534] hover:bg-[#E7F5EC] border-0 text-[10px]">{notif.key}</Badge>
                </div>
                <p className="text-xs text-[#78716C] mt-1">{notif.description}</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white text-left border-b border-[#EDE6D8]">
                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Entity</th>
                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-28" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Enabled</th>
                    {notif.supported_groups.map(g => (
                      <th key={g} className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-36" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {GROUP_LABELS[g] || g}
                      </th>
                    ))}
                    <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-32 text-right" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Test</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EDE6D8]">
                  {rows.map(row => {
                    const n = row.notifications?.[notif.key] || { enabled: false, groups: {} };
                    return (
                      <tr key={row.entity_id} className="hover:bg-[#FCFAF6]/50" data-testid={`notif-row-${notif.key}-${row.entity_id}`}>
                        <td className="px-4 py-3 font-medium text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{row.entity_name}</td>
                        <td className="px-4 py-3">
                          <Switch
                            checked={!!n.enabled}
                            disabled={savingKey.startsWith(`${row.entity_id}:${notif.key}:enabled`)}
                            onCheckedChange={(v) => persist(row.entity_id, notif.key, { enabled: v }, row.entity_name)}
                            data-testid={`notif-toggle-enabled-${notif.key}-${row.entity_id}`}
                          />
                        </td>
                        {notif.supported_groups.map(g => (
                          <td key={g} className="px-4 py-3">
                            <Switch
                              checked={!!n.groups?.[g]}
                              disabled={!n.enabled || savingKey.startsWith(`${row.entity_id}:${notif.key}:groups`)}
                              onCheckedChange={(v) => persist(
                                row.entity_id, notif.key,
                                { groups: { ...(n.groups || {}), [g]: v } },
                                row.entity_name,
                              )}
                              data-testid={`notif-toggle-group-${notif.key}-${row.entity_id}-${g}`}
                            />
                          </td>
                        ))}
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/10 rounded-lg"
                            onClick={() => openTest(notif, row)}
                            data-testid={`notif-test-btn-${notif.key}-${row.entity_id}`}
                          >
                            <PaperPlaneTilt size={13} className="mr-1.5" /> Test
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="notif-test-dialog">
          <DialogHeader>
            <DialogTitle>Send test email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#78716C]">
              Sending <span className="font-semibold text-[#0A2540]">{testCtx.notification?.label}</span>
              {' '}for <span className="font-semibold text-[#0A2540]">{testCtx.entity_name}</span>.
            </p>
            <div className="space-y-1.5">
              <Label>Recipient email</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="steve@ecowaterinternational.com"
                className="border-[#EDE6D8] rounded-lg"
                data-testid="notif-test-email-input"
              />
              <p className="text-xs text-[#78716C]">Yesterday&apos;s live KPIs for this entity will be computed and sent.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)} className="rounded-lg" data-testid="notif-test-cancel-btn">Cancel</Button>
            <Button
              onClick={sendTest}
              disabled={sending}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
              data-testid="notif-test-send-btn"
            >
              <PaperPlaneTilt size={14} className={`mr-2 ${sending ? 'animate-pulse' : ''}`} />
              {sending ? 'Sending…' : 'Send test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
